import { format, formatDistanceToNow } from 'date-fns';
import { ExternalLink, Phone } from 'lucide-react';
import { useState, type CSSProperties } from 'react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/common/page-header';
import { SectionCard } from '@/components/common/section-card';
import { useSparklineBuffer } from '@/components/common/sparkline';
import { StatTile } from '@/components/common/stat-tile';
import { EventIcon } from '@/components/fleet/event-icon';
import { EventSourceBadge } from '@/components/fleet/event-source-badge';
import { LastSeen } from '@/components/fleet/last-seen';
import { DegradedIngressCard } from '@/components/fleet/degraded-ingress-card';
import { ProxyIngressCard } from '@/components/fleet/proxy-ingress-card';
import { RevokeUserSessionsCard } from '@/components/fleet/revoke-user-sessions-card';
import { ServerDrawer } from '@/components/fleet/server-drawer';
import { ServerLink } from '@/components/fleet/server-link';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/fleet/states';
import { Button } from '@/components/ui/button';
import { useFleetEventFeed, type FleetFeedEvent } from '@/hooks/use-fleet-event-feed';
import { useFleetServers } from '@/hooks/use-fleet-servers';
import { useFleetSummary } from '@/hooks/use-fleet-summary';
import { selectStaleServers } from '@/lib/fleet-health';
import type { ServerListItem } from '@/types/fleet';

const ALL_EVENT_TYPES = [
  'provisioned',
  'deployed',
  'updated',
  'restarted',
  'quarantined',
  'blocked',
  'recovered',
  'decommissioned',
  'heartbeat_lost',
  'heartbeat_resumed',
] as const;

// ── Event row ─────────────────────────────────────────────────────────────────

function FeedRow({ event, onClick }: { event: FleetFeedEvent; onClick: () => void }): JSX.Element {
  const occurred = new Date(event.occurred_at);

  return (
    <li
      className="flex items-center gap-3 py-2 cursor-pointer hover:bg-muted/40 rounded px-2 -mx-2 transition-colors"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    >
      <EventIcon eventType={event.event_type} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium capitalize">
            {event.event_type.replace(/_/g, ' ')}
          </span>
          <ServerLink name={event.server_name} className="text-sm" />
          <EventSourceBadge source={event.source} />
        </div>
      </div>
      <span
        className="shrink-0 font-mono text-xs text-muted-foreground"
        title={format(occurred, 'yyyy-MM-dd HH:mm:ss')}
      >
        {formatDistanceToNow(occurred, { addSuffix: true })}
      </span>
    </li>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function FleetDashboardPage(): JSX.Element {
  const summaryQuery = useFleetSummary();
  const serversQuery = useFleetServers({ page: 1, page_size: 200, sort: 'name' });
  const eventsQuery = useFleetEventFeed([...ALL_EVENT_TYPES], 40);
  const [drawerServer, setDrawerServer] = useState<ServerListItem | null>(null);

  const summary = summaryQuery.data;
  const { fresh, total } = summary?.heartbeats ?? { fresh: 0, total: 0 };
  const quarantineCount = summary?.counts.by_status['quarantined'] ?? 0;
  const inFlightCount = summary?.counts.by_status['provisioning'] ?? 0;

  // Count alerts (heartbeat_lost + quarantined) in the last 60 events.
  const alertCount = (eventsQuery.data ?? []).filter(
    (e) => e.event_type === 'heartbeat_lost' || e.event_type === 'quarantined',
  ).length;

  const healthyPct = total === 0 ? 0 : Math.round((fresh / total) * 100);

  const healthyPctBuf = useSparklineBuffer(healthyPct);
  const quarantineBuf = useSparklineBuffer(quarantineCount);
  const inFlightBuf = useSparklineBuffer(inFlightCount);
  const alertBuf = useSparklineBuffer(alertCount);

  const staleServers = serversQuery.data ? selectStaleServers(serversQuery.data.items) : [];

  // Build a map of server name → ServerListItem for drawer
  const serverMap = new Map<string, ServerListItem>(
    (serversQuery.data?.items ?? []).map((s) => [s.name, s]),
  );

  function openDrawerForServer(name: string): void {
    const s = serverMap.get(name);
    if (s) setDrawerServer(s);
  }

  const lastUpdated = summaryQuery.dataUpdatedAt;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fleet health"
        description="The always-on health radiator."
        actions={
          <>
            <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
              <span
                className="signal-dot animate-pulse-dot bg-status-active"
                style={{ ['--bg-dot']: 'var(--status-active)' } as CSSProperties}
                aria-hidden
              />
              Live
            </span>
            {lastUpdated > 0 && (
              <span className="font-mono text-xs text-muted-foreground">
                {formatDistanceToNow(lastUpdated, { addSuffix: true })}
              </span>
            )}
          </>
        }
      />

      {/* 4-column KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="Healthy"
          value={`${fresh}/${total}`}
          sub={`${healthyPct}% heartbeating`}
          spark={healthyPctBuf}
          accent="hsl(150 64% 50%)"
        />
        <StatTile
          label="Quarantine"
          value={String(quarantineCount)}
          sub="servers quarantined"
          spark={quarantineBuf}
          accent="hsl(38 95% 58%)"
        />
        <StatTile
          label="In-flight"
          value={String(inFlightCount)}
          sub="provisioning"
          spark={inFlightBuf}
          accent="hsl(205 92% 60%)"
        />
        <StatTile
          label="Recent alerts"
          value={String(alertCount)}
          sub="heartbeat loss + quarantine"
          spark={alertBuf}
          accent="hsl(0 75% 60%)"
        />
      </div>

      {/* 2-column main */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Events feed — 60% */}
        <SectionCard title="Recent events" className="lg:col-span-3">
          {eventsQuery.isLoading && <TableSkeleton rows={5} />}

          {eventsQuery.isError && (
            <ErrorState
              title="Couldn't load events."
              error={eventsQuery.error}
              onRetry={() => void eventsQuery.refetch()}
            />
          )}

          {eventsQuery.data && eventsQuery.data.length === 0 && (
            <EmptyState message="No recent events" />
          )}

          {eventsQuery.data && eventsQuery.data.length > 0 && (
            <ul className="divide-y">
              {eventsQuery.data.map((event) => (
                <FeedRow
                  key={event.id}
                  event={event}
                  onClick={() => openDrawerForServer(event.server_name)}
                />
              ))}
            </ul>
          )}
        </SectionCard>

        {/* Stale servers — 40% */}
        <SectionCard title="Stale servers" className="lg:col-span-2">
          {serversQuery.isLoading && <TableSkeleton rows={4} />}

          {serversQuery.isError && (
            <ErrorState
              title="Couldn't load the fleet."
              error={serversQuery.error}
              onRetry={() => void serversQuery.refetch()}
            />
          )}

          {serversQuery.data && staleServers.length === 0 && (
            <EmptyState message="All heartbeats fresh" />
          )}

          {serversQuery.data && staleServers.length > 0 && (
            <ul className="divide-y" data-testid="stale-server-list">
              {staleServers.map((server) => (
                <li key={server.id} className="flex items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <ServerLink name={server.name} status={server.status} />
                    <LastSeen timestamp={server.last_seen_at} className="text-xs" />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 shrink-0 text-xs"
                    onClick={() => toast.info(`Paging on-call for ${server.name}…`)}
                  >
                    <Phone className="mr-1 h-3 w-3" aria-hidden />
                    Page on-call
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      {/* Hidden unless something is actually degraded */}
      <DegradedIngressCard />

      {/* Proxy-ingress tier */}
      <ProxyIngressCard />

      {/* Restricted operator actions */}
      <RevokeUserSessionsCard />

      {/* Grafana embed — reference material at bottom */}
      <SectionCard title="Heartbeat metrics">
        <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-md border border-dashed border-border bg-secondary/20 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            Metrics served by Grafana
          </p>
          <a
            href="https://vpn-grafana.getpocketvpn.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            Open in Grafana
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        </div>
      </SectionCard>

      <ServerDrawer server={drawerServer} onClose={() => setDrawerServer(null)} />
    </div>
  );
}
